import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

export interface HeroSlideItem {
  id: string;
  title: string;
  slogan: string;
  backdrop: string;
  summary: string;
  genre: string;
  region: string;
  year: number;
  rating: number;
  duration: string;
  categoryPath: string;
  badge?: string;
}

export default function HeroCarousel({ slides }: { slides: HeroSlideItem[] }) {
  const [index, setIndex] = useState(0);
  const count = slides.length;

  const goTo = useCallback((next: number) => setIndex((next + count) % count), [count]);

  useEffect(() => {
    const timer = setInterval(() => setIndex((i) => (i + 1) % count), 6000);
    return () => clearInterval(timer);
  }, [count]);

  const active = slides[index];

  return (
    <section className="relative w-full h-[560px] md:h-[640px] lg:h-[700px] overflow-hidden bg-background-100">
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={slide.backdrop}
            alt={`${slide.title} 主视觉`}
            title={`${slide.title} 高清在线观看`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/80 to-background-50/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-background-50/50" />
        </div>
      ))}

      <div className="relative z-10 h-full w-full px-4 md:px-6 flex items-center">
        <div key={active.id} className="w-full max-w-2xl animate-fade-up">
          <h1 className="text-xs md:text-sm font-semibold text-primary-400 tracking-[0.2em] mb-3">
            高清影视资源 · 在线观看平台
          </h1>
          <div className="flex items-center gap-3 mb-3">
            {active.badge && (
              <span className="px-2 py-0.5 rounded-md bg-primary-500 text-background-50 text-[11px] font-bold">
                {active.badge}
              </span>
            )}
            <span className="text-sm text-foreground-600">{active.region} · {active.year}</span>
          </div>
          <h2 className="font-heading font-extrabold text-3xl md:text-5xl lg:text-6xl text-foreground-950 leading-tight">
            {active.title}
          </h2>
          <p className="mt-3 text-base md:text-lg text-accent-500 font-heading font-semibold">
            「{active.slogan}」
          </p>
          <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed max-w-xl line-clamp-3">
            {active.summary}
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
            <span className="flex items-center gap-1.5">
              <i className="ri-star-fill text-accent-500" />
              <strong className="text-foreground-950 font-semibold">{active.rating.toFixed(1)}</strong>
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-film-line text-primary-500" />
              {active.genre}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line text-primary-500" />
              {active.duration}
            </span>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${active.id}`}
              className="inline-flex items-center gap-2 px-5 md:px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-fill text-lg" />
              立即观看
            </Link>
            <Link
              to={active.categoryPath}
              className="inline-flex items-center gap-2 px-5 md:px-6 py-3 rounded-md bg-background-200/60 border border-background-300/60 text-foreground-900 text-sm font-semibold hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-apps-2-line text-lg" />
              浏览同类
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => goTo(index - 1)}
        className="hidden md:flex absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-background-50/70 backdrop-blur border border-background-300/60 text-foreground-900 items-center justify-center hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        onClick={() => goTo(index + 1)}
        className="hidden md:flex absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-background-50/70 backdrop-blur border border-background-300/60 text-foreground-900 items-center justify-center hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => goTo(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-8 bg-primary-500" : "w-4 bg-foreground-300/50 hover:bg-foreground-300"
            }`}
            aria-label={`切换到第 ${i + 1} 张`}
          />
        ))}
      </div>
    </section>
  );
}